import React, { Fragment, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Alert, Button, Divider, Popover } from '@fx-ui/fine-design';
import { ECode, EMPTY_FN, get, isNil, map, redirect, UserLoginType, filter } from '@libs/shared';
import { Switch } from '../../icons';
import { SwitchCorpDialog } from './components';
import { checkSuiteAccessible } from '../../apis';
import { BetaTag } from '@fx-ui/jdy-design-biz';
import { getErrorMsg, getSuiteUrl } from '../../utils';
import { authModel } from '../../models';
import { platformIntl } from '../../constants';
import { MonoBusinessSwitchingIcon } from '@fx-ui/icon';
import siteRouterManager, { SiteRouterKey } from '../../utils/site-router';

interface ProductMenuProps {
    onTriggerClick?: () => void;
    onSwitchCorpClick?: () => void;
    onCreateCorpClick?: () => void;
    /**
     * 切换、加入、创建企业成功后，页面跳转前的阻塞型回调
     * 返回 redirectUri 时覆盖默认跳转地址，reject 时中断跳转
     */
    onBeforeCorpAction?: (
        type: 'switch' | 'join' | 'create'
    ) => void | Promise<{ redirectUri?: string }>;
    onMenuClick?: (menuKey: string) => void;
    /**
     * 产品菜单项是否可见
     */
    menuVisible?: Record<string, boolean>;
}

/**
 * 产品菜单
 */
const ProductMenu: React.FC<ProductMenuProps> = (props) => {
    const {
        onTriggerClick = EMPTY_FN,
        onSwitchCorpClick = EMPTY_FN,
        onCreateCorpClick,
        onBeforeCorpAction,
        onMenuClick = EMPTY_FN,
        menuVisible
    } = props;

    const [menuPopoverVisible, setMenuPopoverVisible] = useState(false);
    const [switchCorpVisible, setSwitchCorpVisible] = useState(false);

    const { currentCorpName, userLoginType, isSyncMember, isCurrentUserNoCorp, navigationModules } =
        authModel;

    const allowSwitchCorp = userLoginType !== UserLoginType.sso && !isSyncMember;

    const menus = useMemo(() => {
        return filter(navigationModules, (module) => {
            return menuVisible?.[module.key] !== false;
        });
    }, [menuVisible]);

    const renderMenuContent = () => {
        if (isCurrentUserNoCorp) {
            return (
                <div className="fx-product-menu">
                    <div className="empty-corps-tip">
                        {platformIntl.get('fxp.base.workspace.switch_org.no_joined_org.tip')}
                    </div>
                </div>
            );
        }

        return (
            <div className="fx-product-menu">
                <div className="corp-switch">
                    <div className="current-corp">{currentCorpName}</div>
                    {allowSwitchCorp && (
                        <Button
                            type="text"
                            icon={<Switch />}
                            onClick={() => {
                                setSwitchCorpVisible(true);
                                setMenuPopoverVisible(false);
                                onSwitchCorpClick();
                            }}
                        >
                            {platformIntl.get('switchCorp')}
                        </Button>
                    )}
                </div>
                <div className="entry-list">
                    {map(menus, ({ name, key, url, pc_icon_url, pc_icon_bg, suiteId, is_beta }) => {
                        return (
                            <Fragment key={key}>
                                {key === 'open_platform' && (
                                    <div className="split-wrapper">
                                        <Divider className="split" />
                                    </div>
                                )}
                                <div
                                    className={classNames('entry-item', key)}
                                    onClick={() => {
                                        onMenuClick(key);
                                        if (!isNil(suiteId)) {
                                            return checkSuiteAccessible(suiteId, {
                                                customHandleErrorCodes: [
                                                    ECode.SUITE_CONSENT_NOT_ACTIVATED,
                                                    ECode.TENANT_SUITE_CONSENT_NOT_FOUND,
                                                    ECode.SUITE_CONSENT_CORP_SCOPE_MANAGED_BY_INTEGRATE_SYNC,
                                                    ECode.SUITE_AUTHENTICATION_OUT_OF_SCOPE_IS_SA,
                                                    ECode.SUITE_EXCEED_SCOPE_MEMBER_IS_SA,
                                                    ECode.SUITE_EXCEED_SCOPE_EXTERNAL_MEMBER_IS_SA
                                                ]
                                            })
                                                .then(() => {
                                                    location.href = getSuiteUrl(suiteId);
                                                })
                                                .catch((err) => {
                                                    const errCode = get(err, 'response.data.code');
                                                    if (
                                                        [
                                                            ECode.SUITE_CONSENT_NOT_ACTIVATED,
                                                            ECode.TENANT_SUITE_CONSENT_NOT_FOUND,
                                                            ECode.SUITE_CONSENT_CORP_SCOPE_MANAGED_BY_INTEGRATE_SYNC
                                                        ].includes(errCode)
                                                    ) {
                                                        location.href = getSuiteUrl(suiteId);
                                                    } else if (
                                                        [
                                                            ECode.SUITE_AUTHENTICATION_OUT_OF_SCOPE_IS_SA,
                                                            ECode.SUITE_EXCEED_SCOPE_MEMBER_IS_SA,
                                                            ECode.SUITE_EXCEED_SCOPE_EXTERNAL_MEMBER_IS_SA
                                                        ].includes(errCode)
                                                    ) {
                                                        Alert.openAlert({
                                                            type: 'warning',
                                                            title: getErrorMsg({
                                                                code: errCode
                                                            }),
                                                            text4Cancel:
                                                                platformIntl.get('adjust.no'),
                                                            text4Ok: platformIntl.get('adjust.go'),
                                                            onCancel: () => {
                                                                Alert.closeAlert();
                                                            },
                                                            onOk: () => {
                                                                redirect(
                                                                    siteRouterManager.getPath(
                                                                        SiteRouterKey.ProductCenter,
                                                                        authModel.tenantId
                                                                    )
                                                                );
                                                                Alert.closeAlert();
                                                            }
                                                        });
                                                    }
                                                });
                                        }
                                        if (url) {
                                            location.href = url;
                                        }
                                    }}
                                >
                                    <div className="icon" style={{ background: pc_icon_bg }}>
                                        <img src={pc_icon_url} />
                                    </div>
                                    <div className="text" title={name}>
                                        {name}
                                    </div>
                                    {is_beta && (
                                        <div className="tag-wrapper">
                                            <BetaTag className="entry-tag" />
                                        </div>
                                    )}
                                </div>
                            </Fragment>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <>
            <Popover
                visible={menuPopoverVisible}
                onVisibleChange={setMenuPopoverVisible}
                content={renderMenuContent()}
                arrow={false}
                hasPadding={false}
                offset={[0, 4]}
                placement="bottom-left"
                trigger="click"
            >
                <div className="fx-product-menu-icon" onClick={onTriggerClick}>
                    <MonoBusinessSwitchingIcon />
                </div>
            </Popover>
            <SwitchCorpDialog
                visible={switchCorpVisible}
                onCancel={() => setSwitchCorpVisible(false)}
                onCreateCorp={onCreateCorpClick}
                onBeforeCorpAction={onBeforeCorpAction}
            />
        </>
    );
};

export default ProductMenu;
